let draftKeySeq = 0;
/** Local-only identity for a break the editor is tracking. */
export const nextDraftKey = () => `draft-${(draftKeySeq += 1)}`;
const ms = (iso) => new Date(iso).getTime();
/** Whole minutes between two instants, never negative. */
export const minutesBetween = (startIso, endIso) => Math.max(0, Math.round((ms(endIso) - ms(startIso)) / 60000));
/**
 * Net minutes worked: gross span less every break.
 *
 * All breaks are deducted, paid or not, because that is what the server's
 * net_work_minutes does. Netting only unpaid breaks here would put a different
 * number on the correction screen than on the timesheet it corrects.
 */
export const netMinutes = (start, end, breaks) => {
    if (!end)
        return 0;
    const gross = minutesBetween(start, end);
    const breakTotal = breaks.reduce((sum, b) => sum + minutesBetween(b.start, b.end), 0);
    return Math.max(0, gross - breakTotal);
};
/** Net minutes of a snapshot, skipping breaks that never ended. */
export const snapshotNetMinutes = (snapshot) => netMinutes(snapshot.start_time, snapshot.end_time, (snapshot.breaks ?? []).filter((b) => !!b.end_time).map((b) => ({ start: b.start_time, end: b.end_time })));
/** Seeds the editor from the entry as it stands. */
export const snapshotToDraft = (snapshot) => ({
    start: snapshot.start_time,
    end: snapshot.end_time ?? snapshot.start_time,
    // A break with no end is still running and cannot be meaningfully retimed,
    // so it is left out of the editor rather than shown with a fabricated end.
    breaks: (snapshot.breaks ?? [])
        .filter((b) => !!b.end_time)
        .map((b) => ({
        key: nextDraftKey(),
        breakId: b.id,
        breakTypeId: b.break_type_id ?? undefined,
        name: b.break_type_name || 'Break',
        isPaid: !!b.is_paid,
        start: b.start_time,
        end: b.end_time,
        removed: false,
    })),
});
const sameInstant = (a, b) => {
    if (!a || !b)
        return a === b;
    return ms(a) === ms(b);
};
/**
 * Turns the editor's draft into the payload the API stores — only the parts
 * that actually differ from the snapshot, so an untouched edge is never sent
 * as a change the approver has to read past.
 */
export const draftToProposal = (snapshot, draft) => {
    const proposal = {};
    if (!sameInstant(draft.start, snapshot.start_time))
        proposal.new_start_time = draft.start;
    if (!sameInstant(draft.end, snapshot.end_time))
        proposal.new_end_time = draft.end;
    const original = new Map((snapshot.breaks ?? []).map((b) => [b.id, b]));
    const breakEdits = [];
    const newBreaks = [];
    const deleteBreakIds = [];
    for (const b of draft.breaks) {
        if (b.breakId && b.removed) {
            deleteBreakIds.push(b.breakId);
            continue;
        }
        if (b.removed)
            continue; // an added break the user then dropped: a no-op
        if (!b.breakId) {
            newBreaks.push({
                ...(b.breakTypeId ? { break_type_id: b.breakTypeId, break_type_name: b.name } : {}),
                start_time: b.start,
                end_time: b.end,
            });
            continue;
        }
        const before = original.get(b.breakId);
        const startChanged = !sameInstant(b.start, before?.start_time);
        const endChanged = !sameInstant(b.end, before?.end_time);
        // A break can be re-categorised without moving at all, so the type is
        // compared independently of the times.
        const typeChanged = !!b.breakTypeId && b.breakTypeId !== (before?.break_type_id ?? undefined);
        if (startChanged || endChanged || typeChanged) {
            breakEdits.push({
                break_id: b.breakId,
                ...(startChanged ? { new_start_time: b.start } : {}),
                ...(endChanged ? { new_end_time: b.end } : {}),
                ...(typeChanged ? { new_break_type_id: b.breakTypeId, new_break_type_name: b.name } : {}),
            });
        }
    }
    if (breakEdits.length)
        proposal.break_edits = breakEdits;
    if (newBreaks.length)
        proposal.new_breaks = newBreaks;
    if (deleteBreakIds.length)
        proposal.delete_break_ids = deleteBreakIds;
    return proposal;
};
/** Whether a proposal asks for anything at all. */
export const isProposalEmpty = (proposal) => {
    if (!proposal)
        return true;
    return (!proposal.new_start_time &&
        !proposal.new_end_time &&
        !proposal.break_edits?.length &&
        !proposal.new_breaks?.length &&
        !proposal.delete_break_ids?.length);
};
/**
 * Replays a proposal onto the snapshot to produce the "after".
 *
 * This mirrors what the server does on approval, including dropping breaks
 * left outside the new bounds — so the reviewer sees the day they are actually
 * approving, not an optimistic version of it.
 */
export const applyProposal = (snapshot, proposal) => {
    const start = proposal?.new_start_time ?? snapshot.start_time;
    const end = proposal?.new_end_time ?? snapshot.end_time ?? null;
    const deleted = new Set(proposal?.delete_break_ids ?? []);
    const edits = new Map((proposal?.break_edits ?? []).map((e) => [e.break_id, e]));
    const breaks = [];
    for (const b of snapshot.breaks ?? []) {
        if (deleted.has(b.id))
            continue;
        const edit = edits.get(b.id);
        breaks.push(edit
            ? {
                ...b,
                start_time: edit.new_start_time ?? b.start_time,
                end_time: edit.new_end_time ?? b.end_time,
                break_type_id: edit.new_break_type_id ?? b.break_type_id,
                break_type_name: edit.new_break_type_name ?? b.break_type_name,
            }
            : b);
    }
    for (const nb of proposal?.new_breaks ?? []) {
        breaks.push({
            id: `new-${nb.start_time}`,
            break_type_id: nb.break_type_id ?? null,
            break_type_name: nb.break_type_name || 'Break',
            is_paid: false,
            start_time: nb.start_time,
            end_time: nb.end_time,
        });
    }
    // Breaks the employee did not touch but that the new bounds leave outside
    // the entry are dropped on apply, so they must not appear in the "after".
    const touched = new Set([...edits.keys()]);
    const surviving = breaks.filter((b) => {
        if (touched.has(b.id) || b.id.startsWith('new-'))
            return true;
        if (ms(b.start_time) < ms(start))
            return false;
        if (end && b.end_time && ms(b.end_time) > ms(end))
            return false;
        if (end && ms(b.start_time) > ms(end))
            return false;
        return true;
    });
    surviving.sort((a, b) => ms(a.start_time) - ms(b.start_time));
    return { start_time: start, end_time: end, breaks: surviving };
};
/**
 * Describes a proposal as a list a person can read: every field that moves,
 * old value to new. Used verbatim by the employee ("what you're asking to
 * change") and the approver ("what you're approving").
 */
export const describeChanges = (snapshot, proposal) => {
    const changes = [];
    const after = applyProposal(snapshot, proposal);
    if (!sameInstant(after.start_time, snapshot.start_time)) {
        changes.push({
            key: 'session-start',
            label: 'Checked in',
            oldValue: snapshot.start_time,
            newValue: after.start_time,
            kind: 'session',
        });
    }
    if (!sameInstant(after.end_time, snapshot.end_time)) {
        changes.push({
            key: 'session-end',
            label: 'Checked out',
            oldValue: snapshot.end_time ?? undefined,
            newValue: after.end_time ?? undefined,
            kind: 'session',
        });
    }
    const before = new Map((snapshot.breaks ?? []).map((b) => [b.id, b]));
    const afterIds = new Set(after.breaks.map((b) => b.id));
    for (const b of after.breaks) {
        const original = before.get(b.id);
        if (!original) {
            changes.push({
                key: `break-add-${b.id}`,
                label: `${b.break_type_name || 'Break'} added`,
                newValue: b.start_time,
                kind: 'break_added',
            });
            continue;
        }
        if (!sameInstant(b.start_time, original.start_time)) {
            changes.push({
                key: `break-start-${b.id}`,
                label: `${b.break_type_name || 'Break'} started`,
                oldValue: original.start_time,
                newValue: b.start_time,
                kind: 'break_retimed',
            });
        }
        if (!sameInstant(b.end_time, original.end_time)) {
            changes.push({
                key: `break-end-${b.id}`,
                label: `${b.break_type_name || 'Break'} ended`,
                oldValue: original.end_time ?? undefined,
                newValue: b.end_time ?? undefined,
                kind: 'break_retimed',
            });
        }
        if ((b.break_type_id ?? null) !== (original.break_type_id ?? null)) {
            // Paid vs unpaid changes what the day is worth, so this reads as its own
            // change rather than being folded into a retiming.
            changes.push({
                key: `break-type-${b.id}`,
                label: 'Break category',
                oldValue: original.break_type_name || 'Break',
                newValue: b.break_type_name || 'Break',
                kind: 'break_recategorised',
                isText: true,
            });
        }
    }
    for (const b of snapshot.breaks ?? []) {
        if (!afterIds.has(b.id)) {
            changes.push({
                key: `break-remove-${b.id}`,
                label: `${b.break_type_name || 'Break'} removed`,
                oldValue: b.start_time,
                kind: 'break_removed',
            });
        }
    }
    return changes;
};
export const netImpact = (snapshot, proposal) => {
    const beforeMinutes = snapshotNetMinutes(snapshot);
    const afterMinutes = snapshotNetMinutes(applyProposal(snapshot, proposal));
    return { beforeMinutes, afterMinutes, deltaMinutes: afterMinutes - beforeMinutes };
};
/**
 * Client-side validation, mirroring the server's rules so the employee is told
 * before they submit rather than after. The server revalidates regardless.
 */
export const validateDraft = (draft) => {
    if (!draft.start || !draft.end)
        return 'A check-in and a check-out time are both needed';
    if (ms(draft.end) <= ms(draft.start))
        return 'Check-out must be after check-in';
    const live = draft.breaks.filter((b) => !b.removed);
    for (const b of live) {
        const label = b.name || 'break';
        if (ms(b.end) <= ms(b.start))
            return `The ${label} must end after it starts`;
        if (ms(b.start) < ms(draft.start))
            return `The ${label} starts before you checked in`;
        if (ms(b.end) > ms(draft.end))
            return `The ${label} ends after you checked out`;
    }
    const sorted = [...live].sort((a, b) => ms(a.start) - ms(b.start));
    for (let i = 1; i < sorted.length; i += 1) {
        if (ms(sorted[i - 1].end) > ms(sorted[i].start)) {
            return 'Two breaks overlap — adjust them so they do not run at the same time';
        }
    }
    return null;
};
/**
 * The day a correction request is ABOUT — which is not the day it was filed.
 *
 * A list of reasons with no dates cannot be tied to any actual time, and the
 * filing date is often a different day from the one being corrected. Prefer
 * the frozen snapshot (the entry as it stood), then the proposed bounds for
 * reported missing time. created_at is a last resort and is reported as
 * `exact: false`, so callers can label it "Filed ..." rather than presenting a
 * guess as the corrected day — and can decline to link it to a day at all.
 */
export const correctionAnchor = (c) => {
    const iso = c.original_snapshot?.start_time ||
        c.proposed_start_time ||
        c.proposed_end_time ||
        c.proposed_changes?.new_start_time ||
        c.proposed_changes?.new_breaks?.[0]?.start_time;
    return iso ? { iso, exact: true } : { iso: c.created_at, exact: false };
};
/**
 * Where to put a break the employee is adding.
 *
 * Seeding one at the day's midpoint collides with whatever is already there —
 * a recorded lunch usually sits near the middle — and an overlapping break
 * fails validation with no obvious cause, so the "Add a break" button appears
 * to do nothing. This finds the largest free gap instead, and falls back to
 * the end of the last break when the day is too full to fit the full length.
 */
export const suggestBreakSlot = (draft, minutes = 30) => {
    const dayStart = ms(draft.start);
    const dayEnd = ms(draft.end);
    const want = minutes * 60000;
    const taken = draft.breaks
        .filter((b) => !b.removed)
        .map((b) => ({ start: ms(b.start), end: ms(b.end) }))
        .sort((a, b) => a.start - b.start);
    // Gaps between the day's edges and the breaks already in it.
    const gaps = [];
    let cursor = dayStart;
    for (const t of taken) {
        if (t.start > cursor)
            gaps.push({ start: cursor, end: t.start });
        cursor = Math.max(cursor, t.end);
    }
    if (dayEnd > cursor)
        gaps.push({ start: cursor, end: dayEnd });
    const widest = gaps.sort((a, b) => b.end - b.start - (a.end - a.start))[0];
    if (!widest) {
        // No room at all: sit right after the last break and let the employee
        // adjust, rather than silently overlapping it.
        const from = taken.length ? taken[taken.length - 1].end : dayStart;
        return { start: new Date(from).toISOString(), end: new Date(from + want).toISOString() };
    }
    const width = widest.end - widest.start;
    // Centre it in the gap when it fits; otherwise take the whole gap.
    const start = width > want ? widest.start + (width - want) / 2 : widest.start;
    const end = width > want ? start + want : widest.end;
    return { start: new Date(Math.round(start)).toISOString(), end: new Date(Math.round(end)).toISOString() };
};
